import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import swal from "sweetalert";

import { useTranslation } from "react-i18next";
import { PickList } from "primereact/picklist";
import "primereact/resources/themes/saga-blue/theme.css";

import { inputChange } from "../../helpers";
import { BriefcaseServices } from "./BriefcaseServices";

const FormBriefcase = ({ uid, ddlForm, briefcaseData }) => {
  const [t] = useTranslation("briefcase");
  const history = useHistory();

  const [mainData, setMainData] = useState({ Nombre: "", Descripcion: "" });
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [source, setSource] = useState([]);
  const [target, setTarget] = useState([]);
  const [sourceSelection, setSourceSelection] = useState([]);
  const [targetSelection, setTargetSelection] = useState([]);

  // Hidrata el formulario en modo edición.
  useEffect(() => {
    if (briefcaseData && briefcaseData.portafolio) {
      setMainData({
        Nombre: briefcaseData.portafolio.Nombre || "",
        Descripcion: briefcaseData.portafolio.Descripcion || "",
      });
      const associated = (briefcaseData.proyectos || []).map((prj, idx) => ({
        index: idx,
        label: prj.Nombre,
        value: prj.IdProyecto,
      }));
      setTarget(associated);
    }
  }, [briefcaseData]);

  const searchFilters = async () => {
    try {
      setLoading(true);
      const result = await BriefcaseServices.getDataByFilter(filters);
      const selectedIds = new Set(target.map((item) => item.value));
      const sources = (result || [])
        .filter((prj) => !selectedIds.has(prj.IdProyecto))
        .map((prj, idx) => ({
          index: idx,
          label: prj.Nombre,
          value: prj.IdProyecto,
        }));
      setSource(sources);
    } catch (error) {
      swal("¡Ups!", t("briefcase_modal_done_5"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeColumns = (e) => {
    setSource(e.source);
    setTarget(e.target);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!mainData.Nombre || !mainData.Nombre.trim()) {
      swal("¡Ups!", t("briefcase_validation_name"), "warning");
      return;
    }
    if (!target.length) {
      swal("¡Ups!", t("briefcase_validation_projects"), "warning");
      return;
    }

    const payload = {
      nombre: mainData.Nombre.trim(),
      descripcion: mainData.Descripcion || "",
      proyectos: target.map((item) => item.value),
    };

    try {
      setSaving(true);
      if (uid) {
        await BriefcaseServices.update(uid, payload);
      } else {
        await BriefcaseServices.create(payload);
      }
      swal(
        t("briefcase_modal_done_1"),
        t("briefcase_modal_done_2"),
        "success"
      ).then(() => {
        history.push("/briefcases");
      });
    } catch (error) {
      swal("¡Ups!", t("briefcase_modal_done_5"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSave}>
      {/* SECCIÓN 1: DATOS GENERALES */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="fa fa-fingerprint text-primary mr-2"></i>
            {t("briefcase_title_generaldata")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={12} className="mb-3">
              <Form.Label>{t("briefcase_name")}</Form.Label>
              <Form.Control
                name="Nombre"
                onChange={(e) => inputChange(e, setMainData)}
                value={mainData.Nombre || ""}
                required
              />
            </Col>
            <Col md={12} className="mb-3">
              <Form.Label>{t("briefcase_description")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Descripcion"
                onChange={(e) => inputChange(e, setMainData)}
                value={mainData.Descripcion || ""}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <hr />

      {/* SECCIÓN 2: FILTROS */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="fa fa-filter text-primary mr-2"></i>
            {t("briefcase_filters")}
            <i
              className="fa fa-question-circle text-info ml-2 mt-1"
              style={{ fontSize: "0.85rem" }}
              data-tip={t("briefcase_filters_tooltip")}
            ></i>
          </h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : (
            <Row>
              <Col md={4} className="mb-3">
                <Form.Label>{t("briefcase_sector")}</Form.Label>
                <Form.Control
                  name="tipologia"
                  as="select"
                  value={filters.tipologia || ""}
                  onChange={(e) => inputChange(e, setFilters)}
                >
                  <option value="">{t("briefcase_sector_select")}</option>
                  {_.filter(ddlForm, { DDL: "TIPO_PROYECTO" }).map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.VALOR}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>{t("briefcase_owner")}</Form.Label>
                <Form.Control
                  name="owner"
                  as="select"
                  value={filters.owner || ""}
                  onChange={(e) => inputChange(e, setFilters)}
                >
                  <option value="">{t("briefcase_owner_select")}</option>
                  {_.filter(ddlForm, { DDL: "USUARIOS" }).map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.VALOR}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>{t("briefcase_methodology")}</Form.Label>
                <Form.Control
                  name="methodology"
                  as="select"
                  value={filters.methodology || ""}
                  onChange={(e) => inputChange(e, setFilters)}
                >
                  <option value="">{t("briefcase_methodology_select")}</option>
                  {_.filter(ddlForm, { DDL: "METODOLOGIA" }).map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.VALOR}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>{t("briefcase_country")}</Form.Label>
                <Form.Control
                  name="country"
                  as="select"
                  value={filters.country || ""}
                  onChange={(e) => inputChange(e, setFilters)}
                >
                  <option value="">{t("briefcase_country_select")}</option>
                  {_.filter(ddlForm, { DDL: "PAIS" }).map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.VALOR}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col className="d-flex align-items-end mb-3">
                <Button
                  variant="outline-primary"
                  className="mr-2"
                  style={{ height: "38px" }}
                  onClick={searchFilters}
                  size="sm"
                  title={t("briefcase_button_filter")}
                >
                  <i className="fa far fa-search" />
                </Button>
                <Button
                  variant="outline-secondary"
                  style={{ height: "38px" }}
                  onClick={clearFilters}
                  size="sm"
                  title={t("briefcase_button_clearfilters")}
                >
                  <i className="fa fa-times" />
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      <hr />

      {/* SECCIÓN 3: PICKLIST DE PROYECTOS */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="fa fa-check-double text-primary mr-2"></i>
            {t("briefcase_projects_selection")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={12} className="mt-2">
              <Form.Group>
                <PickList
                  metaKeySelection={false}
                  source={source}
                  target={target}
                  sourceHeader={t("briefcase_picklist_available")}
                  targetHeader={t("briefcase_picklist_selected")}
                  itemTemplate={(item) => <span>{item?.label}</span>}
                  onChange={handleChangeColumns}
                  showTargetControls={true}
                  showSourceControls={true}
                  sourceSelection={sourceSelection}
                  targetSelection={targetSelection}
                  onSourceSelectionChange={(e) => setSourceSelection(e.value)}
                  onTargetSelectionChange={(e) => setTargetSelection(e.value)}
                  filter
                  filterBy="label"
                  sourceFilterPlaceholder={t("briefcase_picklist_search")}
                  targetFilterPlaceholder={t("briefcase_picklist_search")}
                  sourceStyle={{
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    height: "250px",
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Button
        variant="success"
        type="submit"
        className="position-fixed"
        style={{ bottom: "1rem", right: "1rem" }}
        disabled={saving}
      >
        <i className="fa fa-save mr-2"></i>
        {t("briefcase_button_save")}
      </Button>
    </Form>
  );
};

export default FormBriefcase;
